import { memo } from 'react';
import type { WhyItWorksCardProps } from './WhyItWorksCard.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { useTranslation } from 'react-i18next';

/**
 * WhyItWorksCard - Displays the strategic rationale for an idea
 *
 * Features:
 * - Responsive layout (horizontal in fullscreen, vertical in panel mode)
 * - Accessible with proper semantic structure
 * - Memoized to prevent unnecessary re-renders
 */
export const WhyItWorksCard = memo(function WhyItWorksCard({ content }: WhyItWorksCardProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  return (
    <MotionBox
      component="article"
      aria-labelledby="why-it-works-title"
      className={`flex rounded-3xl bg-surface p-10 ${
        isFullscreen ? 'flex-row gap-4' : 'flex-col gap-8'
      }`}
    >
      <h3 id="why-it-works-title" className="min-w-68.75 shrink-0 text-heading-xl text-primary">
        {t('ideationWidget.whyItWorksCard.whyItWorks')}
      </h3>
      <p className="flex-1 text-xl text-foreground">{content}</p>
    </MotionBox>
  );
});
